import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

const conditionSchema = z.object({
  field: z
    .string()
    .describe("JSON path in row_data (e.g., 'price', 'supplier.name')"),
  operator: z.enum([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_null",
    "is_not_null",
    "in",
    "not_in",
    "regex_match",
  ]),
  value: z.unknown().optional(),
});

/**
 * Pipeline Rows Process Step
 *
 * The core classification/routing node. Evaluates configurable rules against
 * each row's data and assigns freeform statuses. Rules use AND logic within
 * each rule, and are evaluated by priority (highest first, first match wins).
 *
 * Use cases:
 * - Classify imported rows into processing buckets (auto_process, needs_review, etc.)
 * - Pre-filter rows by skipping those that don't meet criteria
 * - Route rows to different downstream workflows by status
 *
 * Example workflow:
 * ```
 * Pipeline Rows Insert → Pipeline Rows Process
 *     Rules: [
 *       { conditions: [{field: "amount", operator: "gt", value: 1000}], action: {setStatus: "needs_review"} },
 *       { conditions: [{field: "supplier", operator: "is_not_null"}], action: {setStatus: "auto_process"} },
 *     ]
 * ```
 */
export const pipelineRowsProcessStep = defineStepConfig({
  type: "pipeline_rows_process",
  name: "Process Pipeline Rows",
  description:
    "Classify/route pipeline rows by evaluating configurable rules and assigning statuses",
  category: "Transformation",

  inputSchema: z.object({
    batchId: z
      .string()
      .optional()
      .describe("Process rows from a specific batch only"),
    pipelineId: z
      .string()
      .optional()
      .describe("Override the pipeline ID from config"),
  }),

  outputSchema: z.object({
    processedCount: z.number().describe("Total number of rows evaluated"),
    results: z
      .record(z.string(), z.number())
      .describe(
        'Count of rows per resulting status (e.g., {"auto_process": 45, "needs_review": 12})',
      ),
    unchangedCount: z
      .number()
      .describe("Number of rows left unchanged (no rule matched)"),
  }),

  configSchema: z.object({
    pipelineId: z.string().describe("Pipeline to process"),
    sourceStatusFilter: z
      .array(z.string())
      .prefault(["pending"])
      .describe("Only process rows with these statuses"),
    rules: z
      .array(
        z.object({
          name: z.string().describe("Rule name for audit trail"),
          priority: z
            .number()
            .prefault(0)
            .describe(
              "Higher priority rules are evaluated first. First matching rule wins.",
            ),
          conditions: z
            .array(conditionSchema)
            .describe("All conditions must match (AND logic)"),
          conditionGroups: z
            .array(
              z.object({
                logic: z.enum(["AND", "OR"]),
                conditions: z.array(conditionSchema),
              }),
            )
            .optional()
            .describe(
              "Condition groups with configurable AND/OR logic. Takes precedence over flat conditions.",
            ),
          conditionGroupsLogic: z
            .enum(["AND", "OR"])
            .prefault("OR")
            .describe(
              "How condition groups combine. OR = any group matches, AND = all groups must match.",
            ),
          action: z.object({
            setStatus: z
              .string()
              .describe(
                "Freeform status to assign (e.g., 'auto_process', 'needs_review')",
              ),
            setStatusReason: z.string().optional(),
            mergeMetadata: z
              .record(z.string(), z.unknown())
              .optional()
              .describe("Key-value pairs to merge into row metadata"),
          }),
        }),
      )
      .describe("Rules evaluated in priority order; first match wins per row"),
    defaultAction: z
      .object({
        setStatus: z
          .string()
          .optional()
          .describe(
            "Status to assign if no rules match (omit to leave unchanged)",
          ),
        setStatusReason: z.string().optional(),
      })
      .optional()
      .describe("Action to apply if no rules match a row"),
    batchSize: z
      .number()
      .prefault(100)
      .describe("Number of rows to update per database transaction"),
    limit: z
      .number()
      .prefault(10000)
      .describe("Maximum number of rows to process per run"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
